/**
 * Aggregation der Elternzeile einer Sammelsendung (Plan 2026-10-02-sammelsendung-aggregat-zeile).
 *
 * Reine Funktion ohne React: je Spaltenschluessel (dieselben wie in `Zelle`, `Bestellzeile.tsx`)
 * der Wert, den die Kinder DIESES Bereichs gemeinsam ergeben. Das Ergebnis beschreibt nur, WAS
 * angezeigt wird; gezeichnet wird in `AggregatZellen.tsx` mit denselben Helfern wie in der
 * Kinderzeile (Datum, Pillen).
 *
 * `supplier`, `status`, `value` (und die Bestellnummer-Spalte `name`) liefern `zentral`: dort gilt der
 * zentrale Wert der Sendung (`shipmentParent.sharedValues`, Gesamtbetrag samt Override), nicht eine
 * Ableitung aus den Kindern - den setzt `BoardSeite` ein.
 */
import { fmtDate } from "../../lib/format.ts";
import { eingebuchtZustand, letzteEinbuchungVon } from "../../lib/orderList.ts";
import { orderListDue } from "../../lib/status.ts";
import type { Order } from "../../lib/types.ts";
import { zollinstruktionenAngestossen } from "../../lib/zollinstruktionen.ts";

export type Aggregat =
  /** Nichts zu zeigen: Gedankenstrich (`order-list-empty`). */
  | { art: "leer" }
  /** Kinder weichen ab: Zelle bleibt leer. */
  | { art: "gemischt" }
  /** Der zentrale Wert der Sendung gilt; der Aufrufer zeichnet ihn. */
  | { art: "zentral" }
  | { art: "text"; text: string; titel?: string }
  /** Ein ISO-Datum; `titel` nennt den Bereich, falls die Kinder abweichen. */
  | { art: "datum"; datum: string; titel?: string }
  | { art: "bereich"; von: string; bis: string }
  /** Dieselbe Pille wie in der Kinderzeile: „Zahlung angestoßen" bzw. „Zollinstruktionen angestoßen". */
  | { art: "pille"; pille: "zahlung" | "zoll" }
  /**
   * Die Pille der Spalte „Ware eingebucht" (`EingebuchtPille`): `datum` ist der SPAETESTE Tag der
   * letzten Einbuchung ueber alle Kinder (`null`, wenn keins ein Datum hat), `titel` nennt bei
   * "teilweise" den Anteil der vollstaendig gelieferten Kinder.
   */
  | { art: "eingebucht"; zustand: "vollstaendig" | "teilweise"; datum: string | null; titel?: string };

const ZENTRAL: ReadonlySet<string> = new Set(["name", "supplier", "status", "value"]);

const text = (wert: unknown): string => (wert === null || wert === undefined ? "" : String(wert));

/**
 * Spannweite der nichtleeren Daten; `null`, wenn keines vorhanden ist. Verglichen wird nur der
 * Datumsteil (`YYYY-MM-DD`): "2026-11-01" und "2026-11-01T00:00:00Z" sind dasselbe Datum.
 */
function datumsspanne(daten: readonly string[]): { von: string; bis: string; anzahl: number } | null {
  const vorhanden = daten.filter((d) => d !== "").map((d) => d.slice(0, 10)).sort();
  return vorhanden.length === 0
    ? null
    : { von: vorhanden[0], bis: vorhanden[vorhanden.length - 1], anzahl: vorhanden.length };
}

/** Anzeigewert je Kind, einheitlich -> der Wert, sonst `gemischt`; lauter leere -> `leer`. */
function gleichOderGemischt(werte: readonly string[]): Aggregat {
  if (werte.every((w) => w === "")) return { art: "leer" };
  return werte.every((w) => w === werte[0]) ? { art: "text", text: werte[0] } : { art: "gemischt" };
}

function anteil(schluessel: "zahlung" | "zoll", treffer: number, gesamt: number, teilText: string): Aggregat {
  if (treffer === 0) return { art: "leer" };
  if (treffer === gesamt) return { art: "pille", pille: schluessel };
  return { art: "text", text: `${treffer} von ${gesamt} ${teilText}` };
}

/**
 * „Ware eingebucht" der Elternzeile (Auftrag 2026-10-20): gruen nur, wenn ALLE Kinder vollstaendig
 * eingebucht sind; orange, sobald mindestens ein Kind etwas eingebucht hat (vollstaendig ODER
 * teilweise); sonst Gedankenstrich. Das Datum ist in beiden Faellen die juengste letzte Einbuchung
 * der Kinder. Der fruehere Text „X von Y geliefert" bleibt als `title` der orangen Pille erhalten.
 */
function eingebuchtAggregat(kinder: readonly Order[]): Aggregat {
  if (kinder.length === 0) return { art: "leer" };
  const zustaende = kinder.map((k) => eingebuchtZustand(k));
  const vollstaendig = zustaende.filter((z) => z === "vollstaendig").length;
  const mitEinbuchung = zustaende.filter((z) => z !== "nichts").length;
  if (mitEinbuchung === 0) return { art: "leer" };
  const tage = kinder.map((k) => letzteEinbuchungVon(k)).filter((tag): tag is string => tag !== null);
  const datum = tage.length === 0 ? null : tage.reduce((a, b) => (b > a ? b : a));
  if (vollstaendig === kinder.length) return { art: "eingebucht", zustand: "vollstaendig", datum };
  return {
    art: "eingebucht",
    zustand: "teilweise",
    datum,
    titel: `${vollstaendig} von ${kinder.length} geliefert`,
  };
}

/**
 * Wie viele Kinder vom zentralen Faelligkeitsdatum der Sendung abweichen: verglichen wird nur der
 * Datumsteil (`YYYY-MM-DD`) der Listenfaelligkeit (`orderListDue`); ein Kind ohne Datum zaehlt als
 * abweichend. Board (Elternzelle) und Drawer nennen diese Zahl im Hinweis.
 */
export function faelligkeitsAbweichungen(kinder: readonly Order[], zentral: string): number {
  return kinder.filter((k) => text(orderListDue(k)).slice(0, 10) !== zentral).length;
}

/** „1 Bestellung weicht ab" bzw. „<n> Bestellungen weichen ab". */
export function abweichungsText(n: number): string {
  return n === 1 ? "1 Bestellung weicht ab" : `${n} Bestellungen weichen ab`;
}

/**
 * `zentraleFaelligkeit`: das zentrale Faelligkeitsdatum der Sendung (`sharedValues.listDueDate`). Ist es
 * gesetzt, zeigt die Spalte `due` diesen Wert und nennt abweichende Kinder im Titel; ohne (`null`,
 * `undefined`, leer) wird wie bisher aus den Kindern abgeleitet.
 */
export function aggregiereSpalte(
  schluessel: string,
  kinder: readonly Order[],
  sendungsnummer: string,
  zentraleFaelligkeit?: string | null,
): Aggregat {
  if (ZENTRAL.has(schluessel)) return { art: "zentral" };
  switch (schluessel) {
    case "dsvShipmentNo":
      return sendungsnummer ? { art: "text", text: sendungsnummer } : { art: "leer" };
    case "due": {
      if (zentraleFaelligkeit) {
        const abweichend = faelligkeitsAbweichungen(kinder, zentraleFaelligkeit);
        return abweichend > 0
          ? { art: "datum", datum: zentraleFaelligkeit, titel: abweichungsText(abweichend) }
          : { art: "datum", datum: zentraleFaelligkeit };
      }
      const spanne = datumsspanne(kinder.map((k) => text(orderListDue(k))));
      if (!spanne) return { art: "leer" };
      return spanne.von === spanne.bis
        ? { art: "datum", datum: spanne.von }
        : {
            art: "datum",
            datum: spanne.von,
            titel: `frühestes von ${spanne.anzahl} Terminen, spätestes ${fmtDate(spanne.bis)}`,
          };
    }
    case "invoiceNo": {
      const verschieden = Array.from(new Set(kinder.map((k) => text(k.invoiceNo)).filter((w) => w !== "")));
      if (verschieden.length === 0) return { art: "leer" };
      const verbunden = verschieden.join(", ");
      return { art: "text", text: verbunden, titel: verbunden };
    }
    case "etdPi":
    case "etd":
    case "eta": {
      const spanne = datumsspanne(kinder.map((k) => text(k[schluessel])));
      if (!spanne) return { art: "leer" };
      return spanne.von === spanne.bis
        ? { art: "datum", datum: spanne.von }
        : { art: "bereich", von: spanne.von, bis: spanne.bis };
    }
    case "payment":
      return anteil("zahlung", kinder.filter((k) => k.paymentInitiatedAt).length, kinder.length, "angestoßen");
    case "zoll":
      return anteil("zoll", kinder.filter((k) => zollinstruktionenAngestossen(k)).length, kinder.length, "angestoßen");
    case "booked":
      return eingebuchtAggregat(kinder);
    default:
      return gleichOderGemischt(
        kinder.map((k) => text((k.customFields as Record<string, unknown> | undefined)?.[schluessel])),
      );
  }
}
